import type { StateAbbreviation as DomainStateAbbreviation } from "encamp-domain-constants";
import * as domainConstants from "encamp-domain-constants";
import { LEPC_IDS } from "./governmentAgencyIds";

export const STATE_NAMES = domainConstants.STATE_NAMES;

export const stateNamesByAbbreviation =
  domainConstants.stateNamesByAbbreviation;

export type StateAbbreviation = DomainStateAbbreviation;

export const STATES: StateAbbreviation[] = Object.keys(
  STATE_NAMES
) as StateAbbreviation[];

export const PROVINCE_TERRITORY_NAMES = <const>{
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  NT: "Northwest Territories",
  NU: "Nunavut",
  YT: "Yukon",
};

export const PROVINCES: ProvinceAbbreviation[] = Object.keys(
  PROVINCE_TERRITORY_NAMES
) as ProvinceAbbreviation[];

export type ProvinceAbbreviation = keyof typeof PROVINCE_TERRITORY_NAMES;

export const STATES_AND_PROVINCES = [...STATES, ...PROVINCES];

export const EPLAN_STATES: StateAbbreviation[] = [
  "AL",
  "AK",
  "FL",
  "GA",
  "IA",
  "MS",
  "MT",
  "NY",
  "NC",
  "SC",
  "TN",
];

export const TIER_II_MANAGER_STATES = [
  "AR",
  "CT",
  "DE",
  "IL",
  "IN",
  "KY",
  "MA",
  "MD",
  "MI",
  "MN",
  "ND",
  "NV",
  "OR",
  "PA",
  "WI",
  "WV",
] as const;

export const TIER_II_MANAGER_VA_LEPC = [
  LEPC_IDS.VA.FAIRFAX_COUNTY,
  LEPC_IDS.VA.HENRICO_COUNTY,
  LEPC_IDS.VA.PRINCE_WILLIAM_COUNTY,
] as const;

export const TIER_II_SUBMIT_STATES: StateAbbreviation[] = [
  "AK",
  "AL",
  "CO",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "ME",
  "MS",
  "MT",
  "NC",
  "NH",
  "NM",
  "NY",
  "OH",
  "OK",
  "RI",
  "SC",
  "TN",
  "UT",
  "VA",
  "VI",
  "VT",
  "WY",
];

export const IDIOSYNCRATIC_STATES: StateAbbreviation[] = [
  "AZ",
  "CA",
  "KS",
  "LA",
  "MO",
  "NE",
  "NJ",
  "SD",
  "TX",
  "WA",
];

/**
 * States where credential verification automation is not supported.
 * The verify credentials button will be disabled in the UI,
 * the API will reject job creation, and geppetto2 will gracefully
 * fail any jobs that slip through.
 */
export const VERIFY_CREDENTIALS_DENIED_STATES: StateAbbreviation[] = [
  "CO",
  "HI",
  "UT",
];

/**
 * States that report RCRA data in the RCRAInfo portal
 */
export const RCRA_INFO_STATES = [
  "AK",
  "AR",
  "CO",
  "CT",
  "DE",
  "DC",
  "GA",
  "HI",
  "ID",
  "IN",
  "IA",
  "LA",
  "ME",
  "MD",
  "MA",
  "NE",
  "NV",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "PA",
  "RI",
  "SC",
  "SD",
  "UT",
  "VT",
  "VA",
  "WV",
  "WI",
];

export const BILLING_CONTACT_STATES = Array.from(
  new Set([...EPLAN_STATES, ...TIER_II_SUBMIT_STATES, "CA", "TX"])
);

export const EMERGENCY_EQUIPMENT_CONTACT_STATES: StateAbbreviation[] = [
  "CT",
  "ME",
  "NH",
  "RI",
  "VT",
] as const;

export type Tier2ManagerSearch = {
  // Normalized CAS number, no dashes
  casNumber: string;
  // A commonly used name for this chemical, like an alias;
  // I could see a case for this being an array of strings,
  // Like ["Ammonia (anhydrous)", "Ammonia (anhudrous)"]
  chemicalName: string;
  // Is this chemical an EHS?
  ehs?: boolean;
} & (
  | {
      // The old system calls this 'productId'
      chemicalId: string;
      componentPercent?: never;
    }
  | {
      chemicalId?: never;
      componentPercent: number;
    }
  | {
      chemicalId?: undefined;
      componentPercent?: undefined;
    }
);

type Tier2ManagerSearchMappings = {
  casNumber: string;
  encampChemicalNames: string[];
  ehs?: boolean;
  chemicalId?: string;
  componentPercentageLowerBoundInclusive?: number | undefined;
  componentPercentageUpperBoundInclusive?: number | undefined;

  /**
   * What we are searching for
   */
  t2mChemicalName: string;
};

/**
 * Known search strings from T2M portals, we can use this
 * to make sure our searches succeed in their portal.
 *
 * If this list grows, we should just make 2 dictionaries
 * from this; one for the CAS number and one for the chemicalName.
 */
export const TIER_II_MANAGER_SEARCH_STRINGS: Tier2ManagerSearchMappings[] = [
  {
    casNumber: "7664417",
    encampChemicalNames: [
      "Aqua Ammonia (20-35%)",
      "Ammonia",
      "Ammonia (Conc >20%)",
      "**Ammonia",
      "Prologue", // Nutrien Ag Solutions uses this name
    ],
    t2mChemicalName: "Ammonia",
    ehs: true,
    componentPercentageLowerBoundInclusive: 0,
    componentPercentageUpperBoundInclusive: 100,
  },
  {
    casNumber: "7664417",
    encampChemicalNames: [
      "Anhydrous Ammonia",
      "Anhudrous Ammonia",
      "Pro-Iron 5",
      // https://ehs.encamp.com/o/2f4211bd-510e-4475-bac9-5dbcc94e60d1/chemicals/catalog/chemicals/3721f5c8-722b-526f-baa5-b5a9d3cee351
      // Ammonia, Anhydrous - Duke Energy
      "Ammonia, Anhydrous",
      //https://ehs.encamp.com/o/ddba9da5-4db7-4018-bc67-78d78200022a/chemicals/catalog/chemicals/1a93d750-b176-4169-bd47-0bc85d1e648d
      "Pro Trio",
    ],
    t2mChemicalName: "Ammonia (anhydrous)",
    ehs: true,
  },
  {
    casNumber: "1910425",
    encampChemicalNames: ["Paraquat"],
    t2mChemicalName: "Paraquat dichloride",
    ehs: true,
  },
  {
    casNumber: "2074502",
    encampChemicalNames: ["Paraquat"],
    t2mChemicalName: "Paraquat methosulfate",
    ehs: true,
  },
  {
    //https://ehs.encamp.com/o/1bcb2aed-da3f-4f6d-b83f-ca248dff472d/chemicals/catalog/chemicals/3dfa9f80-2919-4638-b3f1-a995397c2d74
    // EQT - SCAL16098A
    casNumber: "7647010",
    encampChemicalNames: [
      "Hydrochloric Acid",
      "Hydrochloric Acid Solution",
      "HCL 15%",
      "Hydrochloric Acid 15%",
      "Hydrochloric Acide",
      "HCL 10.1-15%",
      "SCAL16098A",
    ],
    t2mChemicalName: "Hydrochloric acid",
    ehs: false,
  },
  {
    casNumber: "7647010",
    encampChemicalNames: ["Hydrogen chloride"],
    t2mChemicalName: "Hydrogen chloride (anhydrous)",
    ehs: true,
  },
  {
    casNumber: "7722841",
    encampChemicalNames: [
      "Hydrogen Peroxide",
      "Hydrogen Peroxide (h2o2)",
      // https://ehs.encamp.com/o/08ddd1b2-9e21-4907-a4c7-520371b73bbd/chemicals/catalog/chemicals/17e4ecfe-a663-44d6-bf10-2014b43f9ea0
      // Thermo Fisher Scientific - Hydrogen Peroxide Solution
      "Hydrogen Peroxide Solution",
      //https://ehs.encamp.com/o/ddba9da5-4db7-4018-bc67-78d78200022a/chemicals/catalog/chemicals/a958a516-1eaa-4314-aed7-c2f26ea7f870
      // Nutrien Ag Solutions - Hydrogen Peroxide (20-40%)
      "Hydrogen Peroxide (20-40%)",
      // https://ehs.encamp.com/o/66579c52-4923-4bcf-8ae8-1502310184c3/chemicals/catalog/chemicals/83737b3f-2211-4cc0-b6a4-ef28a543f15c
      // Rivian - GardobondH7002 (50% hydrogen peroxide)
      "GardobondH7002 (50% hydrogen peroxide)",
      // https://ehs.encamp.com/o/a3e04932-18d2-48a4-a0a6-eb86eab23f93/chemicals/catalog/chemicals/709f4674-8b3d-48a9-b7ca-538a427da932
      // Covetrus - Hydrogen Peroxide 3% strength
      "Hydrogen Peroxide 3% strength",
      // https://ehs.encamp.com/o/238d7a91-39d1-4173-954b-4e1544aca9b5/chemicals/catalog/chemicals/96091a86-1ee4-4ba7-89ef-d7c5bed73cce
      // Tosca - Hydrogen peroxide (Conc.< 52%)
      "Hydrogen peroxide (Conc.< 52%)",
    ],
    t2mChemicalName: "Hydrogen Peroxide (Conc.< 52%)",
    componentPercentageLowerBoundInclusive: 0,
    componentPercentageUpperBoundInclusive: 52,
    ehs: false,
  },
];
